/**
 * Online Food Ordering Cart Drawer
 * Sliding cart panel with upsell banner and checkout CTA
 */

import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  LocalShipping as DeliveryIcon,
  ShoppingBag as EmptyCartIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useOrderFees } from '@/hooks/useOrderFees';
import { useAppStore } from '@/store/useAppStore';
import { useCartStore, useCartSubtotal } from '@/store/useCartStore';
import { OrderType } from '@/types';

export default function CartDrawer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { t } = useTranslation();

  const cartDrawerOpen = useAppStore((state) => state.cartDrawerOpen);
  const setCartDrawerOpen = useAppStore((state) => state.setCartDrawerOpen);
  const restaurant = useAppStore((state) => state.restaurant);

  const items = useCartStore((state) => state.items);
  const orderType = useCartStore((state) => state.orderType);
  const promoCode = useCartStore((state) => state.promoCode);
  const promoDiscount = useCartStore((state) => state.promoDiscount);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const removeItem = useCartStore((state) => state.removeItem);
  const clearCart = useCartStore((state) => state.clearCart);
  const subtotal = useCartSubtotal();

  // Use centralized fee calculation hook
  const { deliveryFee, total, hasFreeDelivery, amountForFreeDelivery } = useOrderFees();

  const showUpsellBanner = orderType === 'delivery' && !hasFreeDelivery && items.length > 0;

  const handleCheckout = () => {
    setCartDrawerOpen(false);
    navigate('/checkout');
  };

  const handleClose = () => {
    setCartDrawerOpen(false);
  };

  return (
    <Drawer
      anchor="right"
      open={cartDrawerOpen}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMobile ? '100%' : 400,
          maxWidth: '100vw',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {t('cart.your_order')}
        </Typography>
        <IconButton onClick={handleClose} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Upsell Banner - consistent styling */}
      <AnimatePresence>
        {showUpsellBanner && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Box
              sx={{
                mx: 2,
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                background: `linear-gradient(90deg, ${alpha(theme.palette.success.main, 0.12)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <DeliveryIcon sx={{ fontSize: 20, color: 'success.main' }} />
                <Typography variant="body2" fontWeight={500}>
                  {t('cart.upsell_delivery', { amount: amountForFreeDelivery.toFixed(2) })}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ pl: 4.5 }}>
                {t('cart.delivery_est')}
              </Typography>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Free delivery achieved */}
      <AnimatePresence>
        {orderType === 'delivery' && hasFreeDelivery && subtotal > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Box
              sx={{
                mx: 2,
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                background: alpha(theme.palette.success.main, 0.12),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <DeliveryIcon sx={{ fontSize: 20, color: 'success.main' }} />
                <Typography variant="body2" fontWeight={600} color="success.main">
                  {t('cart.free_delivery')}
                </Typography>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cart Items */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {items.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              color: 'text.secondary',
            }}
          >
            <EmptyCartIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" gutterBottom>
              {t('cart.basket_empty')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('cart.basket_empty_subtitle')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                handleClose();
                navigate('/menu');
              }}
            >
              {t('cart.browse_menu')}
            </Button>
          </Box>
        ) : (
          <Box>
            <AnimatePresence mode="popLayout">
              {items.map((item) => {
                const modifiersTotal = item.modifiers.reduce(
                  (sum, m) => sum + Number(m.price_adjustment),
                  0
                );
                const lineTotal = (Number(item.unit_price) + modifiersTotal) * item.quantity;

                return (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <Box sx={{ py: 2, borderBottom: 1, borderColor: 'divider' }}>
                      {/* Item name and quantity controls - same row */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          sx={{ flex: 1, minWidth: 0, pr: 1 }}
                        >
                          {item.name}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            flexShrink: 0,
                          }}
                        >
                          {item.quantity === 1 ? (
                            <IconButton
                              size="small"
                              onClick={() => removeItem(item.id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                          )}
                          <Typography variant="body2" sx={{ minWidth: 24, textAlign: 'center' }}>
                            {item.quantity}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Modifiers - full width below */}
                      {item.modifiers.length > 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: '0.75rem',
                            lineHeight: 1.4,
                            wordBreak: 'break-word',
                          }}
                        >
                          + {item.modifiers.map((m) => m.name).join(', ')}
                        </Typography>
                      )}

                      {/* Notes */}
                      {item.notes && (
                        <Typography
                          variant="body2"
                          color="warning.main"
                          sx={{
                            fontSize: '0.75rem',
                            fontStyle: 'italic',
                            mt: 0.5,
                            wordBreak: 'break-word',
                          }}
                        >
                          {item.notes}
                        </Typography>
                      )}

                      {/* Price */}
                      <Typography variant="body2" fontWeight={600} color="primary" sx={{ mt: 0.5 }}>
                        £{lineTotal.toFixed(2)}
                      </Typography>
                    </Box>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </Box>
        )}
      </Box>

      {/* Footer with totals */}
      {items.length > 0 && (
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          {/* Order Type Chip */}
          <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
            <Chip
              label={t('checkout.delivery')}
              variant={orderType === 'delivery' ? 'filled' : 'outlined'}
              color={orderType === 'delivery' ? 'primary' : 'default'}
              onClick={() => useCartStore.getState().setOrderType(OrderType.DELIVERY)}
              size="small"
            />
            <Chip
              label={t('checkout.collection')}
              variant={orderType === 'collection' ? 'filled' : 'outlined'}
              color={orderType === 'collection' ? 'primary' : 'default'}
              onClick={() => useCartStore.getState().setOrderType(OrderType.COLLECTION)}
              size="small"
            />
          </Box>

          {/* Totals */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                {t('checkout.subtotal')}
              </Typography>
              <Typography variant="body2">£{subtotal.toFixed(2)}</Typography>
            </Box>
            {orderType === 'delivery' && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('checkout.delivery_fee')}{' '}
                  <Typography component="span" variant="caption" color="text.disabled">
                    (est.)
                  </Typography>
                </Typography>
                <Typography
                  variant="body2"
                  color={deliveryFee === 0 ? 'success.main' : 'text.primary'}
                >
                  {deliveryFee === 0 ? t('checkout.free') : `£${deliveryFee.toFixed(2)}`}
                </Typography>
              </Box>
            )}
            {promoDiscount > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="success.main">
                  {t('checkout.discount')} ({promoCode})
                </Typography>
                <Typography variant="body2" color="success.main">
                  -£{promoDiscount.toFixed(2)}
                </Typography>
              </Box>
            )}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {t('checkout.total')}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} color="primary">
                £{total.toFixed(2)}
              </Typography>
            </Box>
          </Box>

          {/* Checkout Button */}
          {!restaurant?.is_open && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mb: 1, textAlign: 'center', fontWeight: 600 }}
            >
              {t('cart.restaurant_closed')}
            </Typography>
          )}
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleCheckout}
            disabled={!restaurant?.is_open}
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
            }}
          >
            {restaurant?.is_open ? t('cart.go_to_checkout') : t('cart.currently_closed')}
          </Button>

          {/* Clear Basket */}
          <Button
            variant="text"
            size="small"
            color="inherit"
            fullWidth
            onClick={clearCart}
            sx={{ mt: 1, color: 'text.secondary' }}
          >
            {t('cart.clear_basket')}
          </Button>
        </Box>
      )}
    </Drawer>
  );
}
